/* GDPR cookie consent — first visit, re-asked after 12 months or when storage is
   cleared. Accept all / reject non-essential / customise. Non-essential analytics
   stay OFF until accepted. Choice is stored locally and mirrored to the account. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import api from "@/lib/api";

const KEY = "nc_cookie_consent";
const VERSION = 1;
const TWELVE_MONTHS_MS = 365 * 86400000;

export function getConsent() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (c.v !== VERSION || Date.now() - new Date(c.at).getTime() > TWELVE_MONTHS_MS) return null;
    return c;
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [customise, setCustomise] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    if (window.location.pathname.startsWith("/cef/")) return; // in-game overlays stay clean
    if (!getConsent()) setVisible(true);
  }, []);

  const save = async (choices) => {
    const record = { v: VERSION, at: new Date().toISOString(), choices };
    localStorage.setItem(KEY, JSON.stringify(record));
    setVisible(false);
    try { await api.post("/security/cookie-consent", { choices, version: VERSION }); } catch { /* guests too — local record stands */ }
  };

  if (!visible) return null;

  return (
    <div data-testid="cookie-consent" className="fixed bottom-0 inset-x-0 z-[90] border-t border-white/10 bg-[#0a0a0a]/95 backdrop-blur-md">
      <div className="max-w-5xl mx-auto px-4 py-4 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <Cookie className="w-5 h-5 text-nc-orange shrink-0 mt-0.5" />
          <div>
            <p className="font-sans font-bold uppercase tracking-widest text-sm text-zinc-100">Cookies & tracking</p>
            <p className="font-mono text-[11px] text-zinc-400 leading-relaxed mt-1">
              We use strictly-necessary storage for login sessions and security (always on). Optional analytics cookies help us
              improve the platform and stay off unless you accept them. See the{" "}
              <Link to="/privacy" data-testid="cookie-privacy-link" className="text-nc-blue underline underline-offset-2">Privacy Policy</Link> for what we collect and why.
              Your choice is stored with a timestamp and re-asked every 12 months.
            </p>
          </div>
        </div>
        {customise && (
          <label data-testid="cookie-analytics-row" className="flex items-center gap-3 border border-white/10 bg-black/40 px-3 py-2.5 cursor-pointer">
            <input data-testid="cookie-analytics-toggle" type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="w-4 h-4 accent-[#2E6BFF]" />
            <span className="font-mono text-[10px] text-zinc-300 uppercase tracking-[0.15em]">Analytics cookies (optional — off by default)</span>
          </label>
        )}
        <div className="flex flex-wrap gap-2">
          <button data-testid="cookie-accept-all" onClick={() => save({ essential: true, analytics: true })}
            className="bg-nc-blue hover:bg-nc-blue/80 text-white font-mono text-[10px] font-bold uppercase tracking-[0.2em] px-5 py-2.5 transition-colors">
            Accept all
          </button>
          <button data-testid="cookie-reject" onClick={() => save({ essential: true, analytics: false })}
            className="border border-white/20 hover:border-white/50 text-zinc-200 font-mono text-[10px] font-bold uppercase tracking-[0.2em] px-5 py-2.5 transition-colors">
            Reject non-essential
          </button>
          {customise ? (
            <button data-testid="cookie-save-custom" onClick={() => save({ essential: true, analytics })}
              className="border border-nc-orange/50 text-nc-orange font-mono text-[10px] font-bold uppercase tracking-[0.2em] px-5 py-2.5 transition-colors">
              Save my choices
            </button>
          ) : (
            <button data-testid="cookie-customise" onClick={() => setCustomise(true)}
              className="border border-white/20 hover:border-white/50 text-zinc-400 font-mono text-[10px] font-bold uppercase tracking-[0.2em] px-5 py-2.5 transition-colors">
              Customise
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
